/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from "react";
import { Helmet } from "react-helmet-async";
import { Switch, Route, BrowserRouter } from "react-router-dom";

import { GlobalStyle } from "styles/global-styles";
import { useClarity } from "utils/hooks/useClarity";

import { Login } from "./pages/LoginPage/Loadable";
import { LogoutPage } from "./pages/LogoutPage/Loadable";
import { NotFoundPage } from "./components/NotFoundPage/Loadable";
import { HomePage } from "./pages/HomePage/Loadable";
import { BITESHomePage } from "./pages/BITESHomePage/Loadable";
import { BITESHomePageSearchJob } from "./pages/BITESHomePageSearchJob/Loadable";
import { LandingPage } from "./pages/LandingPage/Loadable";
import { AboutUs } from "./pages/AboutUs/Loadable";
import { TermsAndConditions } from "./pages/TermsAndConditions/Loadable";
import { PrivacyPolicy } from "./pages/PrivacyPolicy/Loadable";
import { CancellationRefund } from "./pages/CancellationRefund/Loadable";
import { ShipppingDelivery } from "./pages/ShipppingDelivery/Loadable";
import { ContactUs } from "./pages/ContactUs/Loadable";

// Student Flow
import { StudentDashboard } from "./pages/StudentDashboard/Loadable";
import { MES } from "./pages/StudentMES/Loadable";
import { StudentSearchJob } from "./pages/StudentSearchJob/Loadable";
import { StudentJobsAndDrives } from "./pages/student/job-placement-drives/StudentJobsAndDrives/Loadable";
import { StudentJobDetail } from "./pages/student/job-placement-drives/StudentJobDetail/Loadable";
import { StudentProfile } from "./pages/StudentProfile/Loadable";
import { StudentCommunity } from "./pages/StudentCommunity/Loadable";
import { StudentAllCommunity } from "./pages/StudentAllCommunity/Loadable";
import { StudentMyApplication } from "./pages/StudentMyApplications/Loadable";
import { StudentFavourites } from "./pages/StudentFavourites/Loadable";
import { StudentNotifications } from "./pages/StudentNotifications/Loadable";
import { StudentResumeTemplates } from "./pages/StudentResumeTemplates/Loadable";
import { AddTranscript } from "./pages/StudentProfile/TranscriptLoadable";
import { StudentUpskilling } from "./pages/StudentUpskilling/Loadable";
import { BuyCourse } from "./pages/BuyCourse/Loadable";
import { PaymentProcessing } from "./pages/PaymentProcessing/Loadable";
import { PaymentSuccess } from "./pages/PaymentSuccess/Loadable";
import { PaymentError } from "./pages/PaymentError/Loadable";
import { PaymentCancelled } from "./pages/PaymentCancelled/Loadable";
import { PaymentFailure } from "./pages/PaymentFailure/Loadable";
import PaymentCallbackHandler from "./pages/PaymentCallback";
import TransactionCompleted from "./pages/TransactionCompleted";

// Generic Payment Flow
import GenericPaymentSuccess from "./pages/PaymentFlows/Generic/GenericPaymentSuccess";
import GenericPaymentFailure from "./pages/PaymentFlows/Generic/GenericPaymentFailure";  
import GenericPaymentCancelled from "./pages/PaymentFlows/Generic/GenericPaymentCancelled";

// Campus Flow
import { CampusNotifications } from "./pages/CampusNotifications/Loadable";
import { CampusDashboard } from "./pages/CampusDashboard/Loadable";
import { CampusStudents } from "./pages/CampusStudents/Loadable";
import { CampusProfile } from "./pages/CampusProfile/Loadable";
import { CampusBulkUpload } from "./pages/CampusBulkUpload/Loadable";
import { CampusIndustryConnect } from "./pages/CampusIndustryConnect/Loadable";
import { CampusFacultyDevelopment } from "./pages/CampusFacultyDevelopment/Loadable";
import { CampusIndustryImmersionProgram } from "./pages/CampusIndustryImmersionProgram/Loadable";
import { CampusIndustryConsultingProject } from "./pages/CampusIndustryConsultingProject/Loadable";
import { CampusAddEditStudents } from "./pages/CampusAddEditStudents/Loadable";
import { CampusKYC } from "./pages/CampusKYC/Loadable";
import { CampusStudentActivity } from "./pages/CampusStudentActivity/Loadable";
import { CampusServiceOfferings } from "./pages/CampusServiceOfferings/Loadable";
import { CampusIndustryConnectViewAllJobs } from "./pages/CampusIndustryConnectViewAllJobs/Loadable";

// Employer Flow
import { CompanyProfile } from "./pages/EmployerProfile/Loadable";
import { EmployerDashBoard } from "./pages/EmployerDashboard/Loadable";
import { MatchProfile } from "./pages/StudentMatchProfile/Loadable";
import { EmployerInternshipDetails } from "./pages/EmployerInternshipDetails/Loadable";
import { ViewInternshipDetails } from "./pages/EmployerViewInternshipDetails/Loadable";
import { EmployerAppliedCandidates } from "./pages/EmployerAppliedCandidates/Loadable";
import { EmployerCollegeConnect } from "./pages/EmployerCollegeConnect/Loadable";
import { EmployerCollegeStudentList } from "./pages/EmployerCollegeStudentList/Loadable";
import { EmployerCollegeList } from "./pages/EmployerCollegeList/Loadable";
import { AddEmployerJobs } from "./pages/EmployerAddJobs/Loadable";
import { EmployerJobDetails } from "./pages/EmployerJobsDetails/Loadable";
import { EmployerJobDraft } from "./pages/EmployerJobDraft/Loadable";
import { EmployerJobViewDetails } from "./pages/EmployerJobDetails";
import { EmployerJobAppliedCandidate } from "./pages/EmployerJobAppliedCandidates/Loadable";
import { EmployerSearchCandidates } from "./pages/EmployerSearchCandidates/Loadable";
import { EmployerKYC } from "./pages/EmployerKYC/Loadable";
import { EmployerNotifications } from "./pages/EmployerNotifications/Loadable";
import { SetPassword } from "./pages/SetPasswordPage/Loadable";

// Admin Flow
import { AdminEntities } from "./pages/AdminEntities/Loadable";
import { AdminNewRegistrations } from "./pages/AdminNewRegistrations/Lodable";
import { AdminCommunities } from "./pages/AdminCommunities/Loadable";

import AppApolloprovider from "./ApolloProvider";
import { useTranslation } from "react-i18next";
import getStore from "store/zustandStore";
import { Backdrop, CircularProgress } from "@mui/material";
import ResumeTemplate from "./components/ResumeTemplate";
import TranscriptsDetails from "./components/TranscriptsDetails";
import ProtectedRoute from "./ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import { StudentProfileCompletion } from "./pages/StudentProfile/LoadableProfileCompletion";
import { ForgotPassword } from "./pages/ForgotPassword/Loadable";
import { StudentRegistration } from "./pages/StudentRegistration/Loadable";
import { CollegeRegistration } from "./pages/CollegeRegistration/Loadable";
import { CompanyRegistration } from "./pages/CompanyRegistration/Loadable";
import { StudentResumeTemplate } from "./pages/StudentResumeAddEdit/Loadable";
import { StudentMESPsychometricDetails } from "./pages/StudentMESPsychometricDetails/Loadable";
import { StudentMESAptitudeDetails } from "./pages/StudentMESAptitudeDetails/Loadable";
import { StudentMESAcademicDetails } from "./pages/StudentMESAcademicDetails/Loadable";
// import { NoInternetPage } from "./components/NoInternetPage/Loadable";
import { UnderMaintenancePage } from "./components/UnderMaintenancePage/Loadable";
import { EmployerAccessPrivileges } from "./pages/EmployerAccessPrivileges/Loadable";
import { AdminDashboard } from "./pages/AdminDashboard/Loadable";
import { AdminEmployers } from "./pages/AdminEmployers/Loadable";
import { AdminInstitutions } from "./pages/AdminInstitutions/Loadable";
import { AdminStudents } from "./pages/AdminStudents/Loadable";
import { AdminJobsDetails } from "./pages/AdminJobsDetails/Loadable";
import { AdminJobDetails } from "./pages/AdminJobDetails/Loadable";
import { AdminJobAppliedCandidates } from "./pages/AdminJobAppliedCandidates/Loadable";
import { AdminBroadCast } from "./pages/AdminBroadCast/Loadable";
import { AdminAddEditBroadCast } from "./pages/AdminAddEditBroadCast/Loadable";
import { AdminManageUsers } from "./pages/AdminManageUsers/Loadable";
import { AdminProfile } from "./pages/AdminProfile/Loadable";
import { AdminPriceSetup } from "./pages/AdminPriceSetup/Loadable";
import { AdminActivityLogs } from "./pages/AdminActivityLogs/Loadable";
import { AdminNotifications } from "./pages/AdminNotifications/Loadable";
import { AdminCourses } from "./pages/AdminCourses/Loadable";
import { AdminCourseDetails } from "./pages/AdminCourseDetails/Loadable";
import { AdminCourseCandidates } from "./pages/AdminCourseCandidates/Loadable";
import { AdminImportJobs } from "./pages/AdminImportJobs/Loadable";
import { StudentSelfcare } from "./pages/StudentSelfcare";
import { StudentSelfcareDetails } from "./pages/StudentSelfcareDetails";
import { StudentMESPurchaseDetails } from "./pages/StudentMESPurchaseDetails/Loadable";

// Placement Drives
import { AdminPlacementDashboard, AdminViewDrive } from "./pages/admin/placement-drives/Loadable";
import { 
  EmployerPlacementDashboard,
  EmployerCreateDrive,
  EmployerDriveDetails,
  EmployerDriveJobs,
  EmployerCreateCommunication
} from "./pages/employer/placement-drives/Loadable";
import { 
  CampusPlacementDashboard,
  CampusDriveDetails,
  CampusJobRounds,
  CampusJobDetails
} from "./pages/campus/placement-drives/Loadable";
import { 
  StudentPlacementDashboard,
  StudentDriveDetails,
  StudentJobDetails as StudentPlacementJobDetails
} from "./pages/student/job-placement-drives/Loadable";

// Job Share
import { JobShareHandler } from "./pages/JobShare/Loadable";

export function App() {
  const { i18n } = useTranslation();
  const loading = getStore((state: any) => state.loading);
  const [online, setOnline] = React.useState(true);

  // Initialize Microsoft Clarity tracking
  useClarity();

  React.useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Set initial status
    setOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (!online) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#f5f5f5",
          fontSize: '24px'
        }}
      >
        <p>It seems that your internet connection is lost. Please check your internet connection and try again.</p>
      </div>
    );
  }

  if (window.location.href !== process.env.REACT_APP_GRAPHQL_HTTP_URI) {
    const url = new URL(window.location.href);
    const redirectParam = url.searchParams.get("redirect");
    if (redirectParam) {
      localStorage.setItem("redirecturl", window.location.href);
    }
  }


  return (
    <BrowserRouter>
      <Helmet
        // titleTemplate="%s - React Boilerplate"
        defaultTitle="Prosculpt Campus Connect"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content="Prosculpt jobs" />
      </Helmet>
      <AppApolloprovider>
        <ScrollToTop />
        <Switch>
          {/* <Route exact path="/" component={HomePage} /> */}
          <Route exact path="/" component={Login} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/log-out" component={LogoutPage} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/setpassword" component={SetPassword} />
          <Route
            exact
            path="/student-registration"
            component={StudentRegistration}
          />
          <Route
            exact
            path="/college-registration"
            component={CollegeRegistration}
          />
          <Route
            exact
            path="/company-registration"
            component={CompanyRegistration}
          />
          <Route exact path="/search-jobs" component={BITESHomePageSearchJob} />
          <Route exact path="/about-us" component={AboutUs} />
          <Route
            exact
            path="/terms-and-conditions"
            component={TermsAndConditions}
          />
          <Route exact path="/privacy-policy" component={PrivacyPolicy} />
          <Route
            exact
            path="/cancellation-refund"
            component={CancellationRefund}
          />
          <Route
            exact
            path="/shipping-delivery"
            component={ShipppingDelivery}
          />
          <Route exact path="/contact-us" component={ContactUs} />
          
          {/* Job Share - Universal job sharing route */}
          <Route exact path="/job-share/:jobId" component={JobShareHandler} />
          {/* Catch malformed job-share URLs */}
          <Route path="/job-share" component={JobShareHandler} />

          {/* Student Flow */}
          <ProtectedRoute
            exact
            path="/student-dashboard"
            component={StudentDashboard}
            authenticationPath="/"
            roles={["student"]}
          />
          {/* Disabled route - /student-search-jobs-and-internships is not accessible */}
          {/* <ProtectedRoute
            exact
            path="/student-search-jobs-and-internships"
            component={StudentSearchJob}
            authenticationPath="/"
            roles={["student"]}
          /> */}
          <ProtectedRoute
            exact
            path="/student/job-campus-drives/job-detail/:job_id"
            component={StudentJobDetail}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            path="/student/job-campus-drives"
            component={StudentJobsAndDrives}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mafoi-employability-score"
            component={MES}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-aptitude-details/:assessmentId"
            component={StudentMESAptitudeDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-psychometric-details/:assessmentId"
            component={StudentMESPsychometricDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-academic-details/:assessmentId"
            component={StudentMESAcademicDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-match-profile/:job_id"
            component={MatchProfile}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-profile"
            component={StudentProfile}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-profile-completion"
            component={StudentProfileCompletion}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-events"
            component={StudentCommunity}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-allcommunity"
            component={StudentAllCommunity}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-myapplication"
            component={StudentMyApplication}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-selfcare"
            component={StudentSelfcare}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-selfcare-details"
            component={StudentSelfcareDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-purchase-details"
            component={StudentMESPurchaseDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-favourites"
            component={StudentFavourites}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-notifications"
            component={StudentNotifications}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-add-transcript/:index"
            component={AddTranscript}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-resume-templates"
            component={StudentResumeTemplates}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-resume-builder/:templateId"
            component={StudentResumeTemplate}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-upskilling"
            component={StudentUpskilling}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id"
            component={BuyCourse}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payments/processing"
            component={PaymentProcessing}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payments/success"
            component={PaymentSuccess}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payments/error"
            component={PaymentError}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payments/cancelled"
            component={PaymentCancelled}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payment/success"
            component={PaymentSuccess}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payment/failure"
            component={PaymentFailure}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/buy-course/:course_id/payment/cancelled"
            component={PaymentCancelled}
            authenticationPath="/"
            roles={["student"]}
          />

          {/* Payment Callback Routes - Universal handler shows ONLY loader and auto-closes */}
          <Route
            exact
            path="/buy-course/:course_id/payments/callback/success"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/buy-course/:course_id/payments/callback/failure"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/buy-course/:course_id/payment/callback/success"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/buy-course/:course_id/payment/callback/failure"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/buy-course/:course_id/payments/callback/cancelled"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/buy-course/:course_id/payment/callback/cancelled"
            component={PaymentCallbackHandler}
          />

          {/* Transaction Completed Routes - New gateway URL from server */}
          <Route
            exact
            path="/buy-course/:course_id/transaction_completed"
            component={TransactionCompleted}
          />

          {/* Generic Payment Flow Routes - Support all services */}
          <ProtectedRoute
            exact
            path="/*/payment/success"
            component={GenericPaymentSuccess}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/*/payment/failure"
            component={GenericPaymentFailure}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/*/payment/cancelled"
            component={GenericPaymentCancelled}
            authenticationPath="/"
            roles={["student"]}
          />
          
          {/* Backward compatibility routes for selfcare */}
          <ProtectedRoute
            exact
            path="/student-selfcare/payment/success"
            component={GenericPaymentSuccess}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-selfcare/payment/failure"
            component={GenericPaymentFailure}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-selfcare/payment/cancelled"
            component={GenericPaymentCancelled}
            authenticationPath="/"
            roles={["student"]}
          />
          {/* Selfcare Payment Callback Routes - Universal handler shows ONLY loader and auto-closes */}
          <Route
            exact
            path="/student-selfcare/payment/callback/success"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/student-selfcare/payment/callback/failure"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/student-selfcare/payment/callback/cancelled"
            component={PaymentCallbackHandler}
          />
          {/* Selfcare Transaction Completed Routes - New gateway URL from server */}
          <Route
            exact
            path="/student-selfcare/transaction_completed"
            component={TransactionCompleted}
          />

          {/* MES Purchase Payment Flow Routes - Following same pattern as selfcare */}
          <ProtectedRoute
            exact
            path="/student-mes-purchase-details/payment/success"
            component={GenericPaymentSuccess}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-purchase-details/payment/failure"
            component={GenericPaymentFailure}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student-mes-purchase-details/payment/cancelled"
            component={GenericPaymentCancelled}
            authenticationPath="/"
            roles={["student"]}
          />
          {/* MES Payment Callback Routes - Universal handler shows ONLY loader and auto-closes */}
          <Route
            exact
            path="/student-mes-purchase-details/payment/callback/success"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/student-mes-purchase-details/payment/callback/failure"
            component={PaymentCallbackHandler}
          />
          <Route
            exact
            path="/student-mes-purchase-details/payment/callback/cancelled"
            component={PaymentCallbackHandler}
          />
          {/* MES Transaction Completed Routes - New gateway URL from server */}
          <Route
            exact
            path="/student-mes-purchase-details/transaction_completed"
            component={TransactionCompleted}
          />

          {/* Legacy Buy Service Routes - Backend still uses old pattern */}
          <Route
            exact
            path="/buy-service/:service_id/transaction_completed"
            component={TransactionCompleted}
          />

          {/* Employer Flow */}
          <ProtectedRoute
            exact
            path="/employer-profile"
            component={CompanyProfile}
            authenticationPath="/"
            roles={["employer_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-access-privileges"
            component={EmployerAccessPrivileges}
            authenticationPath="/"
            roles={["employer_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-dashboard"
            component={EmployerDashBoard}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-internship"
            component={EmployerInternshipDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-internship-details"
            component={ViewInternshipDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-applied-candidates"
            component={EmployerAppliedCandidates}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-job-details"
            component={EmployerJobDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-job-view-details/:job_id"
            component={EmployerJobViewDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-job-appliedcanditate/:job_id"
            component={EmployerJobAppliedCandidate}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-college-connect"
            component={EmployerCollegeConnect}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-college-connect-students-list/:campusId"
            component={EmployerCollegeStudentList}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-college-list/:campusId"
            component={EmployerCollegeList}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-add-job/:job_id"
            component={AddEmployerJobs}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-search-job"
            component={EmployerJobDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-job-draft"
            component={EmployerJobDraft}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-search-candidates"
            component={EmployerSearchCandidates}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-notifications"
            component={EmployerNotifications}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer-kyc"
            component={EmployerKYC}
            authenticationPath="/"
            roles={["employer_user"]}
          />

          {/* Resume */}
          <Route
            exact
            path="/resume-template/:student_id"
            component={ResumeTemplate}
          />
          <Route
            exact
            path="/transcripts-details/:student_id"
            component={TranscriptsDetails}
          />

          {/* Campus Flow */}
          <ProtectedRoute
            exact
            path="/campus-dashboard"
            component={CampusDashboard}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-students"
            component={CampusStudents}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-profile"
            component={CampusProfile}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-bulk-update-students"
            component={CampusBulkUpload}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-industry-connect"
            component={CampusIndustryConnect}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-faculty-development"
            component={CampusFacultyDevelopment}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-industry-immersion-program"
            component={CampusIndustryImmersionProgram}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-industry-consulting-project"
            component={CampusIndustryConsultingProject}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-add-edit-student/:student_id"
            component={CampusAddEditStudents}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-kyc"
            component={CampusKYC}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-student-activity"
            component={CampusStudentActivity}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-service-offerings"
            component={CampusServiceOfferings}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-industry-connect-all-jobs/:employerId"
            component={CampusIndustryConnectViewAllJobs}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus-notifications"
            component={CampusNotifications}
            authenticationPath="/"
            roles={["college"]}
          />

          {/* Admin Flow */}
          <ProtectedRoute
            exact
            path="/admin-dashboard"
            component={AdminDashboard}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-employers"
            component={AdminEmployers}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-institutions"
            component={AdminInstitutions}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-students"
            component={AdminStudents}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-search-job"
            component={AdminJobsDetails}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-job-view-details/:job_id"
            component={AdminJobDetails}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-job-appliedcanditate/:job_id"
            component={AdminJobAppliedCandidates}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-broadcast"
            component={AdminBroadCast}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-add-broadcast/:broadcastId"
            component={AdminAddEditBroadCast}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-manage-users"
            component={AdminManageUsers}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-profile"
            component={AdminProfile}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-price-setup"
            component={AdminPriceSetup}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-activity-logs"
            component={AdminActivityLogs}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-notifications"
            component={AdminNotifications}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-courses"
            component={AdminCourses}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-course-details/:courseId"
            component={AdminCourseDetails}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-course-candidates/:courseId"
            component={AdminCourseCandidates}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-import-jobs"
            component={AdminImportJobs}
            authenticationPath="/"
            roles={["super_admin"]}
          />

          {/* Placement Drives Routes */}
          <ProtectedRoute
            exact
            path="/admin/placement-drives/:driveId"
            component={AdminViewDrive}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin/placement-drives"
            component={AdminPlacementDashboard}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives"
            component={EmployerPlacementDashboard}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives/create"
            component={EmployerCreateDrive}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives/:driveId/edit"
            component={EmployerCreateDrive}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives/:driveId"
            component={EmployerDriveDetails}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives/:driveId/job/:jobId"
            component={EmployerDriveJobs}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/employer/placement-drives/:driveId/job/:jobId/:roundId/create-communication"
            component={EmployerCreateCommunication}
            authenticationPath="/"
            roles={["employer_user", "sub_user"]}
          />
          <ProtectedRoute
            exact
            path="/campus/placement-drives"
            component={CampusPlacementDashboard}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus/placement-drives/:driveId"
            component={CampusDriveDetails}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus/placement-drives/:driveId/job/:jobId"
            component={CampusJobDetails}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/campus/placement-drives/:driveId/job/:jobId/rounds"
            component={CampusJobRounds}
            authenticationPath="/"
            roles={["college"]}
          />
          <ProtectedRoute
            exact
            path="/student/job-placement-drives"
            component={StudentPlacementDashboard}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student/job-placement-drives/:driveId"
            component={StudentDriveDetails}
            authenticationPath="/"
            roles={["student"]}
          />
          <ProtectedRoute
            exact
            path="/student/job-placement-drives/:driveId/jobs/:jobId"
            component={StudentPlacementJobDetails}
            authenticationPath="/"
            roles={["student"]}
          />

          {/* <ProtectedRoute
            exact
            path="/admin-new-registrations"
            component={AdminNewRegistrations}
            authenticationPath="/"
            roles={["super_admin"]}
          />
          <ProtectedRoute
            exact
            path="/admin-community"
            component={AdminCommunities}
            authenticationPath="/"
            roles={["super_admin"]}
          /> */}
          {/* <Route exact path="/no-internet" component={NoInternetPage} /> */}
          <Route
            exact
            path="/under-maintenance"
            component={UnderMaintenancePage}
          />
          <Route component={NotFoundPage} />
        </Switch>
      </AppApolloprovider>
      <GlobalStyle />
      <Backdrop
        open={loading || false}
        sx={{
          color: "#000000",
          opacity: "70%",
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <CircularProgress />
      </Backdrop>
    </BrowserRouter>
  );
}
